/* eslint-disable consistent-return */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// react component plugin for creating beatiful tags on an input
import TagsInput from 'react-tagsinput';
// @material-ui/icons
import Close from '@material-ui/icons/Close';
import Eye from '@material-ui/icons/ThumbUp';
import Check from '@material-ui/icons/Check';
import Person from '@material-ui/icons/Person';
import Picture from '@material-ui/icons/Photo';
import ArrowDown from '@material-ui/icons/ArrowDownward';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import Calendar from '@material-ui/icons/CalendarToday';
import BotScoreIco from '@material-ui/icons/Adb';
import Location from '@material-ui/icons/Room';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import Switch from '@material-ui/core/Switch/Switch';
import Select from '@material-ui/core/Select/Select';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// our components
import { adStatus, matchedRules } from 'config';
import Datepicker from 'components/common/DatePicker';
import CategorySelect from 'components/common/CategorySelect';
import LocationSelect from 'components/common/LocationSelect';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import WithPermission from 'components/User/WithPermission';

/**
 * Variable to define default selected status
 */
const defaultSelectedStatus = [
  adStatus.PENDING_PROCESSING,
  adStatus.PENDING_VERIFICATION,
  adStatus.TO_REVIEW,
  adStatus.PROCESSING,
  adStatus.PUBLISHED_BY_MODERATOR,
  adStatus.PUBLISHED_BY_PROCESSOR,
  adStatus.UNPUBLISHED_BY_MODERATOR,
  adStatus.UNPUBLISHED_BY_PROCESSOR,
  adStatus.UNPUBLISHED_BY_USER,
  adStatus.UNPUBLISHED_BY_USER_DELETED,
  adStatus.UNPUBLISHED_BY_USER_BLOCKED,
];

/**
 * @class
 * @extends React.Component
 */
class AdFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      selectedAdsStatus:
        (appliedFilter && appliedFilter.statusIn) || defaultSelectedStatus,
      localFilter: appliedFilter || null,
      expandFiltersAnimation: false,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   * @param {*} callback - Callback waiting for setState.
   */
  handleChangeFilter = (changedFilter, callback = null) => {
    const { localFilter } = this.state;
    this.setState(
      { localFilter: { ...localFilter, ...changedFilter } },
      callback,
    );
  };

  /**
   * @description
   * Update selected status.
   *
   * @param {*} callback - Callback waiting for setState.
   */
  updateStatus = callback => {
    const { selectedAdsStatus } = this.state;
    this.handleChangeFilter(
      selectedAdsStatus.length === 9 || selectedAdsStatus.length === 0
        ? { statusIn: undefined }
        : { statusIn: selectedAdsStatus },
      callback,
    );
  };

  /**
   * @description
   * Handle select status checkboxes.
   *
   * @param {string} checkLabel - Status selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleStatusCheck = (checkLabel, callback = null) => {
    const { selectedAdsStatus } = this.state;
    if (selectedAdsStatus.indexOf(checkLabel) !== -1) {
      const newSelected = selectedAdsStatus.filter(
        status => status !== checkLabel,
      );
      this.setState({ selectedAdsStatus: newSelected }, () =>
        this.updateStatus(callback),
      );
    } else {
      this.setState(
        {
          selectedAdsStatus: [...selectedAdsStatus, checkLabel],
        },
        () => this.updateStatus(callback),
      );
    }
  };

  /**
   * @description
   * Call filter callback.
   */
  handleFilter = () => {
    const { filter } = this.props;
    const { localFilter } = this.state;
    filter({ ...localFilter });
  };

  /**
   * @description
   * Render tag for filter
   *
   * @param {Object} data - An object.
   * @param {string} data.field - Tag label.
   * @param {Object} data.clearVal - Object to call with handleChangeFilter.
   * @param {boolean} data.status - Selected status.
   */
  FilterTag = data => {
    const { classes } = this.props;
    const { localFilter } = this.state;
    const { field, clearVal, status } = data;
    return (
      <div className={classes.collapsedFilterTag} key={`tag-filter-${field}`}>
        {data.icon && <data.icon style={{ height: '0.7em' }} />}
        {field}
        {clearVal && (
          <Button
            justIcon
            simple
            color="info"
            className="clear"
            onClick={() => {
              if (status) {
                this.handleStatusCheck(status, this.handleFilter);
              } else {
                this.setState(
                  {
                    localFilter: { ...localFilter, ...clearVal },
                  },
                  this.handleFilter,
                );
              }
            }}
          >
            <div className={classes.backColor} />
            <Close />
          </Button>
        )}
      </div>
    );
  };

  /**
   * @description
   * Render filters
   *
   * @param {Array} fields - Fields to render filter.
   */
  renderMultipleFilters = fields => {
    const { appliedFilter } = this.props;
    const filters = [];
    if (appliedFilter) {
      fields.forEach(field => {
        if (
          Object.keys(appliedFilter).some(
            filter => filter === field && appliedFilter[filter] !== undefined,
          )
        ) {
          appliedFilter[field].forEach(item => {
            const filterItem = {
              field: item,
              clearVal: {
                [field]: appliedFilter[field].filter(f => f !== item),
              },
            };
            if (field === 'statusIn') {
              filterItem.status = item;
            }
            filters.push(filterItem);
          });
        }
      });
      return filters.map(filter => this.FilterTag(filter));
    }
  };

  /**
   * @description
   * Render filters
   *
   * @param {Array} fields - Fields to render filter.
   * @returns {Array} - Mapped fields to render filters.
   */
  renderStringFilters = fields => {
    const { appliedFilter } = this.props;
    const filters = [];
    if (appliedFilter) {
      Object.keys(fields).forEach(item => {
        if (
          Object.keys(appliedFilter).some(
            filter => filter === item && appliedFilter[filter] !== undefined,
          )
        ) {
          const icon = fields[item][0];
          const prefix = fields[item][1];
          const filterVal = {
            field: prefix
              ? `${prefix} "${appliedFilter[item]}"`
              : appliedFilter[item],
            clearVal: {},
            icon,
          };
          filterVal.clearVal[item] = undefined;
          filters.push(filterVal);
        }
      });
      return filters.map(filter => this.FilterTag(filter));
    }
    return null;
  };

  /**
   * @description
   * Render string filters
   *
   * @param {Array} fields - Fields to render filter.
   * @returns {Array} - Mapped fields to render filters.
   */
  renderBoolFilters = fields => {
    const { appliedFilter } = this.props;
    const filters = [];
    if (appliedFilter) {
      Object.keys(fields).forEach(item => {
        if (
          Object.keys(appliedFilter).some(
            filter => filter === item && appliedFilter[item] !== undefined,
          )
        ) {
          filters.push();
          const filterVal = {
            field: appliedFilter[item] ? fields[item][0] : fields[item][1],
            clearVal: {},
            icon: fields[item][3],
          };
          filterVal.clearVal[item] = undefined;
          if (item === 'reviewed') {
            filterVal.clearVal = {
              ...filterVal.clearVal,
              reviewerId: undefined,
              reviewedBy: undefined,
              reviewedOnGte: undefined,
              reviewedOnLte: undefined,
            };
          }
          filters.push(filterVal);
        }
      });
      return filters.map(filter => this.FilterTag(filter));
    }
    return null;
  };

  /**
   * @description
   * Render boolean filters
   *
   * @param {Array} fields - Fields to render filter.
   * @returns {Array} - Mapped fields to render filters.
   */
  renderEntityFilters = fields => {
    const { appliedFilter } = this.props;
    const filters = [];
    if (appliedFilter) {
      Object.keys(fields).forEach(item => {
        if (
          Object.keys(appliedFilter).some(
            filter => filter === item && appliedFilter[item] !== undefined,
          )
        ) {
          const filterVal = {
            field: fields[item][0](appliedFilter[item]),
            clearVal: {},
            icon: fields[item][1],
          };
          filterVal.clearVal[item] = undefined;
          if (item === 'category') {
            filterVal.clearVal.subcategory = undefined;
          }
          if (item === 'province') {
            filterVal.clearVal.municipality = undefined;
          }
          filters.push(filterVal);
        }
      });
      return filters.map(filter => this.FilterTag(filter));
    }
    return null;
  };

  /**
   * @description
   * Render date time filters
   *
   * @param {Array} fields - Fields to render filter.
   * @returns {Array} - Mapped fields to render filters.
   */
  renderDateTimeFilters = fields => {
    const { appliedFilter } = this.props;
    const filters = [];
    if (appliedFilter) {
      fields.forEach(item => {
        if (
          Object.keys(appliedFilter).some(
            filter =>
              filter === `${item}Gte` &&
              appliedFilter[filter] !== undefined &&
              appliedFilter[filter] !== null,
          )
        ) {
          const filterVal = {
            field: moment(appliedFilter[`${item}Gte`]).format('DD/MM/YYYY'),
            clearVal: {},
            icon: Calendar,
          };
          filterVal.clearVal[`${item}Gte`] = null;
          filters.push(filterVal);
        }
        if (
          Object.keys(appliedFilter).some(
            filter =>
              filter === `${item}Lte` &&
              appliedFilter[filter] !== undefined &&
              appliedFilter[filter] !== null,
          )
        ) {
          const filterVal = {
            field: moment(appliedFilter[`${item}Lte`]).format('DD/MM/YYYY'),
            clearVal: {},
            icon: Calendar,
          };
          filterVal.clearVal[`${item}Lte`] = null;
          filters.push(filterVal);
        }
      });
      return filters.map(filter => this.FilterTag(filter));
    }
  };

  /**
   * @description
   * Function to expand animation.
   * @returns {*} - Null.
   */
  expandAnimation = () => {
    const { expandFiltersAnimation } = this.state;
    const { showFilters } = this.props;

    this.setState({ expandFiltersAnimation: !expandFiltersAnimation }, () =>
      setTimeout(showFilters, 150),
    );
  };

  /**
   * @description
   * Render full filter's box.
   * @returns {*} - AdFilter's box.
   */
  renderFilters() {
    const {
      classes,
      restartFilters,
      categories,
      users,
      t,
      provinces,
    } = this.props;
    const {
      selectedAdsStatus,
      localFilter,
      expandFiltersAnimation,
    } = this.state;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation ? classes.expandFilters : classes.collapseFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('ad.filters.category')}>
              <div className={classes.categorySection}>
                <CategorySelect
                  appliedFilter={localFilter}
                  changeFilter={this.handleChangeFilter}
                  categories={categories}
                />
              </div>
            </SectionDivider>

            <SectionDivider label={t('ad.filters.body.section')}>
              <GridItem>
                <div className={classes.section}>
                  <CustomInput
                    data-cy="adsFilterTitleDescription"
                    id="titleDescription"
                    formControlProps={{
                      className: `${classes.top} ${classes.search}`,
                    }}
                    inputProps={{
                      placeholder: t('ad.filters.body.title_placeholder'),
                      inputProps: {
                        className: classes.searchInput,
                        size: '100%',
                      },
                      onChange: e => {
                        const { value } = e.target;
                        this.setState({
                          localFilter: {
                            ...localFilter,
                            titleDescription: value || undefined,
                          },
                        });
                      },
                      type: 'text',
                      value:
                        localFilter.titleDescription !== undefined
                          ? localFilter.titleDescription
                          : '',
                    }}
                  />
                </div>

                <GridContainer>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      data-cy="adsFilterId"
                      id="id"
                      formControlProps={{
                        className: `${classes.top} ${classes.search}`,
                      }}
                      inputProps={{
                        placeholder: t('ad.filters.body.id_placeholder'),
                        inputProps: {
                          'aria-label': 'Search',
                          className: classes.searchInput,
                        },
                        onChange: e => {
                          const { value } = e.target;
                          this.handleChangeFilter({
                            id: value || undefined,
                          });
                        },
                        value:
                          localFilter.id !== undefined ? localFilter.id : '',
                        type: 'number',
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <div>
                      <span>{t('ad.filters.body.only_images')}</span>
                      <Switch
                        id="images-switch"
                        checked={localFilter.hasImage !== undefined}
                        onChange={() => {
                          if (localFilter.hasImage === true) {
                            this.handleChangeFilter({
                              hasImage: undefined,
                            });
                          } else
                            this.handleChangeFilter({
                              hasImage: true,
                            });
                        }}
                        classes={{
                          checked: classes.switchChecked,
                          icon: classes.switchIcon,
                          iconChecked: classes.switchIconChecked,
                          bar: classes.switchBar,
                        }}
                      />
                    </div>
                  </GridItem>
                </GridContainer>

                <GridContainer>
                  <GridItem xs={12} md={8}>
                    <CustomInput
                      data-cy="adsFilterEmail"
                      id="emailTerm"
                      formControlProps={{
                        className: `${classes.top} ${classes.search}`,
                      }}
                      inputProps={{
                        placeholder: t('ad.filters.body.email_placeholder'),
                        inputProps: {
                          className: classes.searchInput,
                          size: '100%',
                        },
                        onChange: e => {
                          const { value } = e.target;
                          this.handleChangeFilter({
                            emailTerm: value || undefined,
                          });
                        },
                        value:
                          localFilter.emailTerm !== undefined
                            ? localFilter.emailTerm
                            : '',
                        type: 'text',
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={4}>
                    <CustomInput
                      data-cy="adsFilterPhone"
                      id="phoneTerm"
                      formControlProps={{
                        className: `${classes.top} ${classes.search}`,
                      }}
                      inputProps={{
                        placeholder: t('ad.filters.body.phone_placeholder'),
                        inputProps: {
                          className: classes.searchInput,
                          size: '100%',
                        },
                        onChange: e => {
                          const { value } = e.target;
                          this.handleChangeFilter({
                            phoneTerm: value || undefined,
                          });
                        },
                        value:
                          localFilter.phoneTerm !== undefined
                            ? localFilter.phoneTerm
                            : '',
                        type: 'text',
                      }}
                    />
                  </GridItem>
                </GridContainer>

                <GridContainer>
                  <h6 className={`${classes.title} ${classes.locationTitle}`}>
                    {t('ad.filters.location')}
                  </h6>
                  <div className={classes.categorySection}>
                    <LocationSelect
                      provinces={provinces}
                      selectedProvince={localFilter.province}
                      selectedMunicipality={localFilter.municipality}
                      changeFilter={({ province, municipality }) => {
                        if (typeof province !== 'undefined') {
                          this.handleChangeFilter({
                            province: province !== 0 ? province : undefined,
                            municipality: undefined,
                          });
                        }
                        if (typeof municipality !== 'undefined') {
                          this.handleChangeFilter({
                            municipality:
                              municipality !== 0 ? municipality : undefined,
                          });
                        }
                      }}
                    />
                  </div>
                </GridContainer>
              </GridItem>
            </SectionDivider>
          </GridItem>

          <GridItem xs={12} md={6}>
            <SectionDivider label={t('ad.filters.status.section')}>
              <GridItem>
                <GridContainer>
                  <GridItem xs={12} md={5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterPendingProcessing"
                          className={classes.checked}
                          onClick={() =>
                            this.handleStatusCheck(adStatus.PENDING_PROCESSING)
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.PENDING_PROCESSING,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id="pendingProcessingCheckbox"
                      label={t('ad.filters.status.pending_processing')}
                      style={{
                        width: '100%',
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterPendingVerification"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.PENDING_VERIFICATION,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.PENDING_VERIFICATION,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="pendingVerifiyingCheckbox"
                      label={t('ad.filters.status.pending_verification')}
                      style={{
                        width: '100%',
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterToReview"
                          onClick={() =>
                            this.handleStatusCheck(adStatus.TO_REVIEW)
                          }
                          checked={
                            selectedAdsStatus.indexOf(adStatus.TO_REVIEW) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="toReviewCheckbox"
                      label={t('ad.filters.status.to_review')}
                      style={{
                        width: '100%',
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterProcessing"
                          onClick={() =>
                            this.handleStatusCheck(adStatus.PROCESSING)
                          }
                          checked={
                            selectedAdsStatus.indexOf(adStatus.PROCESSING) !==
                            -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="processingCheckbox"
                      label={t('ad.filters.status.processing')}
                      style={{
                        width: '100%',
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={7}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterPublishedByModerator"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.PUBLISHED_BY_MODERATOR,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.PUBLISHED_BY_MODERATOR,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="publishedByModeratorCheckbox"
                      label={t('ad.filters.status.published_by_moderator')}
                      className={classes.statusChecks}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterPublishedByProccesor"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.PUBLISHED_BY_PROCESSOR,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.PUBLISHED_BY_PROCESSOR,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="publishedByProccesorCheckbox"
                      label={t('ad.filters.status.published_by_proccesor')}
                      className={classes.statusChecks}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterUnpublishedByModerator"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.UNPUBLISHED_BY_MODERATOR,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.UNPUBLISHED_BY_MODERATOR,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="unpublishedByModeratorCheckbox"
                      label={t('ad.filters.status.unpublished_by_moderator')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterUnpublishedByproccesor"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.UNPUBLISHED_BY_PROCESSOR,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.UNPUBLISHED_BY_PROCESSOR,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="unpublishedByproccesorCheckbox"
                      label={t('ad.filters.status.unpublished_by_proccesor')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterUnpublishedByUser"
                          onClick={() =>
                            this.handleStatusCheck(adStatus.UNPUBLISHED_BY_USER)
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.UNPUBLISHED_BY_USER,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="unpublishedByUserCheckbox"
                      label={t('ad.filters.status.unpublished_by_user')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterUnpublishedByUserDeleted"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.UNPUBLISHED_BY_USER_DELETED,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.UNPUBLISHED_BY_USER_DELETED,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="unpublishedByUserCheckbox"
                      label={t('ad.filters.status.unpublished_by_user_deleted')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-cy="adsFilterUnpublishedByUserBlocked"
                          onClick={() =>
                            this.handleStatusCheck(
                              adStatus.UNPUBLISHED_BY_USER_BLOCKED,
                            )
                          }
                          checked={
                            selectedAdsStatus.indexOf(
                              adStatus.UNPUBLISHED_BY_USER_BLOCKED,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="unpublishedByUserBlockedCheckbox"
                      label={t('ad.filters.status.unpublished_by_user_blocked')}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridContainer justify="flex-end">
                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.buttons}
                    onClick={() =>
                      this.setState(
                        {
                          selectedAdsStatus:
                            selectedAdsStatus.length > 0
                              ? []
                              : [
                                  adStatus.PENDING_PROCESSING,
                                  adStatus.PENDING_VERIFICATION,
                                  adStatus.TO_REVIEW,
                                  adStatus.PROCESSING,
                                  adStatus.PUBLISHED_BY_MODERATOR,
                                  adStatus.PUBLISHED_BY_PROCESSOR,
                                  adStatus.UNPUBLISHED_BY_MODERATOR,
                                  adStatus.UNPUBLISHED_BY_PROCESSOR,
                                  adStatus.UNPUBLISHED_BY_USER,
                                  adStatus.UNPUBLISHED_BY_USER_DELETED,
                                  adStatus.UNPUBLISHED_BY_USER_BLOCKED,
                                ],
                        },
                        this.updateStatus,
                      )
                    }
                    id="unselectAll"
                    key="unselectAll"
                    data-cy="adsFilterUnselectAll"
                  >
                    {selectedAdsStatus.length > 0
                      ? t('ad.filters.status.uncheck')
                      : t('ad.filters.status.check')}
                  </Button>
                </GridItem>
              </GridContainer>
            </SectionDivider>
            <SectionDivider
              label={t('ad.filters.date.section')}
              style={{ padding: '17px 8px 14px' }}
            >
              <GridItem>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <Datepicker
                      data-cy="adsFilterCreatedOnDateFrom"
                      title={t('ad.filters.date.from')}
                      defaultValue={t('ad.filters.date.from_placeholder')}
                      onChange={date => {
                        if (
                          localFilter.updatedOnLte &&
                          localFilter.updatedOnLte < date
                        ) {
                          return;
                        }
                        this.handleChangeFilter({
                          updatedOnGte: date
                            ? moment(date)
                                .startOf('day')
                                .format()
                            : null,
                        });
                      }}
                      value={
                        localFilter.updatedOnGte !== undefined &&
                        localFilter.updatedOnGte !== null
                          ? moment(localFilter.updatedOnGte)
                          : ''
                      }
                      toToday
                      validGte={localFilter.updatedOnLte}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <Datepicker
                      data-cy="adsFilterCreatedOnDateTo"
                      title={t('ad.filters.date.to')}
                      defaultValue={t('ad.filters.date.to_placeholder')}
                      onChange={date => {
                        if (
                          localFilter.updatedOnGte &&
                          localFilter.updatedOnGte > date
                        ) {
                          return;
                        }
                        this.handleChangeFilter({
                          updatedOnLte: date
                            ? moment(date)
                                .endOf('day')
                                .format()
                            : null,
                        });
                      }}
                      value={
                        localFilter.updatedOnLte !== undefined &&
                        localFilter.updatedOnLte !== null
                          ? moment(localFilter.updatedOnLte)
                          : ''
                      }
                      toToday
                      validLte={localFilter.updatedOnGte}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
            </SectionDivider>
            <SectionDivider label={t('ad.filters.rules.section')}>
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <Select
                      multiple
                      value={
                        localFilter.multipleSelectRules
                          ? localFilter.multipleSelectRules
                          : []
                      }
                      onChange={e =>
                        this.handleChangeFilter({
                          multipleSelectRules: e.target.value,
                        })
                      }
                      MenuProps={{ className: classes.selectMenu }}
                      inputProps={{
                        name: 'multipleSelectedMatchedRule',
                        id: 'multipleSelectedMatchedRule',
                      }}
                    >
                      {Object.keys(matchedRules).map(rule => (
                        <MenuItem
                          key={matchedRules[rule].key}
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={matchedRules[rule].value}
                        >
                          {matchedRules[rule].key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <TagsInput
                    value={
                      localFilter.rulesParams ? localFilter.rulesParams : []
                    }
                    onChange={value => {
                      this.handleChangeFilter({
                        rulesParams: value,
                      });
                    }}
                    inputProps={{
                      placeholder: t('ad.filters.rules.placeholder'),
                    }}
                    tagProps={{ className: 'react-tagsinput-tag info' }}
                  />
                </GridItem>
              </GridContainer>
            </SectionDivider>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('ad.filters.user.section')}>
              <GridItem>
                <div className={`${classes.row} ${classes.userSection}`}>
                  <div className={classes.buttonGroup}>
                    <Button
                      data-cy="adsFilterHasUser"
                      color="info"
                      round
                      className={classes.firstButton}
                      simple={localFilter.hasUser !== undefined}
                      onClick={() =>
                        this.handleChangeFilter({
                          hasUser: undefined,
                        })
                      }
                      id="user-all"
                      key="user-all"
                    >
                      {t('ad.filters.user.all')}
                    </Button>
                    <Button
                      data-cy="adsFilterWithoutUser"
                      color="info"
                      round
                      className={classes.middleButton}
                      simple={
                        localFilter.hasUser === undefined ||
                        localFilter.hasUser === true
                      }
                      onClick={() =>
                        this.handleChangeFilter({
                          hasUser: false,
                        })
                      }
                      id="user-without-user"
                      key="user-without-user"
                    >
                      {t('ad.filters.user.without')}{' '}
                      <span> {t('ad.filters.user.label')}</span>
                    </Button>
                    <Button
                      data-cy="adsFilterWithUser"
                      color="info"
                      round
                      className={classes.lastButton}
                      simple={
                        localFilter.hasUser === undefined ||
                        localFilter.hasUser === false
                      }
                      onClick={() =>
                        this.handleChangeFilter({
                          hasUser: true,
                        })
                      }
                      id="user-with-user"
                      key="user-with-user"
                    >
                      {t('ad.filters.user.with')}{' '}
                      <span> {t('ad.filters.user.label')}</span>
                    </Button>
                  </div>
                </div>
                {localFilter.hasUser !== false && (
                  <WithPermission allowPermissions={['view_user']}>
                    <div>
                      <CustomInput
                        formControlProps={{
                          className: `${classes.top} ${classes.search}`,
                        }}
                        inputProps={{
                          placeholder: 'ID',
                          inputProps: {
                            'aria-label': 'Search',
                            className: classes.searchInput,
                            size: 80,
                          },
                          onChange: e => {
                            const { value } = e.target;
                            this.handleChangeFilter({
                              userId: value,
                            });
                          },
                          value:
                            localFilter.userId !== undefined
                              ? localFilter.userId
                              : '',
                        }}
                        id="userId"
                        data-cy="adsFilteruserId"
                      />
                    </div>
                  </WithPermission>
                )}
                <div>
                  <CustomInput
                    formControlProps={{
                      className: `${classes.top} ${classes.search}`,
                    }}
                    inputProps={{
                      placeholder: t('ad.filters.user.placeholder'),
                      inputProps: {
                        'aria-label': 'Search',
                        className: classes.searchInput,
                        size: 80,
                      },
                      onChange: e => {
                        const { value } = e.target;
                        this.handleChangeFilter({
                          contactInfo: value,
                        });
                      },
                      value:
                        localFilter.contactInfo !== undefined
                          ? localFilter.contactInfo
                          : '',
                    }}
                    id="contactInfo"
                    data-cy="adsFilterContactInfo"
                  />
                </div>
              </GridItem>
            </SectionDivider>
            <SectionDivider label={t('ad.filters.others.section')}>
              <GridItem>
                <WithPermission
                  allowPermissions={[
                    'view_autopaidad',
                    'view_onwership_autopaidad',
                  ]}
                >
                  <div className={`${classes.row} ${classes.userSection}`}>
                    <h6 className={classes.title} style={{ width: '100%' }}>
                      {t('ad.filters.others.ar')}
                    </h6>
                    <div className={classes.buttonGroup}>
                      <Button
                        color="info"
                        simple={
                          localFilter.isPaidAd === undefined ||
                          localFilter.isPaidAd === false
                        }
                        round
                        className={classes.firstButton}
                        onClick={() =>
                          this.handleChangeFilter({
                            isPaidAd: true,
                          })
                        }
                        id="auto-only"
                        key="auto-only"
                      >
                        {t('ad.filters.others.only')}
                      </Button>
                      <Button
                        color="info"
                        simple={localFilter.isPaidAd !== undefined}
                        round
                        className={classes.middleButton}
                        onClick={() =>
                          this.handleChangeFilter({
                            isPaidAd: undefined,
                          })
                        }
                        id="auto-only-include"
                        key="auto-only-include"
                      >
                        {t('ad.filters.others.include')}
                      </Button>
                      <Button
                        color="info"
                        round
                        className={classes.lastButton}
                        simple={
                          localFilter.isPaidAd === undefined ||
                          localFilter.isPaidAd === true
                        }
                        onClick={() =>
                          this.handleChangeFilter({
                            isPaidAd: false,
                          })
                        }
                        id="auto-only-exclude"
                        key="auto-only-exclude"
                      >
                        {t('ad.filters.others.exclude')}
                      </Button>
                    </div>
                  </div>
                </WithPermission>

                <div className={`${classes.row} ${classes.userSection}`}>
                  <div className={classes.row}>
                    <h6 className={classes.title} style={{ marginRight: 4 }}>
                      {t('ad.filters.others.ip')}
                    </h6>
                    <CustomInput
                      data-cy="adsFilterIp"
                      id="ip"
                      formControlProps={{
                        className: `${classes.top} ${classes.search}`,
                        style: {
                          marginRight: 8,
                        },
                      }}
                      inputProps={{
                        placeholder: '000.000.000.000',
                        inputProps: {
                          className: classes.searchInput,
                        },
                        onChange: e => {
                          const { value } = e.target;
                          this.handleChangeFilter({
                            ip: value || undefined,
                          });
                        },
                        value:
                          localFilter.ip !== undefined ? localFilter.ip : '',
                      }}
                    />
                  </div>
                  <div className={classes.row}>
                    <h6 className={classes.title}>
                      {t('ad.filters.others.botScore')}
                    </h6>
                    <CustomInput
                      data-cy="adsFilterBotScore"
                      id="botScore"
                      inputProps={{
                        placeholder: '0.0',
                        inputProps: {
                          style: {
                            width: 20,
                            marginLeft: 8,
                          },
                        },
                        onChange: e => {
                          const { value } = e.target;
                          this.handleChangeFilter({
                            botScore: value || undefined,
                          });
                        },
                        value:
                          localFilter.botScore !== undefined
                            ? localFilter.botScore
                            : '',
                      }}
                    />
                  </div>

                  <div className={classes.row}>
                    <h6 className={classes.title} style={{ paddingLeft: 20 }}>
                      {t('ad.filters.others.reported')}
                    </h6>
                    <Switch
                      id="reported-switch"
                      checked={localFilter.hasFlag !== undefined}
                      onChange={() => {
                        if (localFilter.hasFlag === true) {
                          this.handleChangeFilter({
                            hasFlag: undefined,
                          });
                        } else
                          this.handleChangeFilter({
                            hasFlag: true,
                          });
                      }}
                      classes={{
                        checked: classes.switchChecked,
                        icon: classes.switchIcon,
                        iconChecked: classes.switchIconChecked,
                        bar: classes.switchBar,
                      }}
                    />
                  </div>
                </div>
              </GridItem>
            </SectionDivider>
          </GridItem>
          <WithPermission allowPermissions={['review_ad']}>
            <GridItem xs={12} md={6}>
              <SectionDivider label={t('ad.filters.reviewed.section')}>
                <GridItem>
                  <div className={`${classes.row} ${classes.userSection}`}>
                    <div className={classes.buttonGroup}>
                      <Button
                        data-cy="adsFilterReviewedNo"
                        color="info"
                        round
                        className={classes.firstButton}
                        simple={
                          localFilter.reviewed === undefined ||
                          localFilter.reviewed === true
                        }
                        onClick={() =>
                          this.handleChangeFilter({
                            reviewed: false,
                            reviewerId: undefined,
                            reviewedBy: undefined,
                            reviewedOnGte: undefined,
                            reviewedOnLte: undefined,
                          })
                        }
                        id="review-no"
                        key="review-no"
                      >
                        {t('ad.filters.reviewed.no')}
                      </Button>
                      <Button
                        data-cy="adsFilterReviewedIgnore"
                        color="info"
                        round
                        className={classes.middleButton}
                        simple={localFilter.reviewed !== undefined}
                        onClick={() =>
                          this.handleChangeFilter({
                            reviewed: undefined,
                            reviewerId: undefined,
                            reviewedBy: undefined,
                            reviewedOnGte: undefined,
                            reviewedOnLte: undefined,
                          })
                        }
                        id="review-ignore"
                        key="review-ignore"
                      >
                        {t('ad.filters.reviewed.ignore')}
                      </Button>
                      <Button
                        data-cy="adsFilterReviewedYes"
                        color="info"
                        round
                        className={classes.lastButton}
                        simple={
                          localFilter.reviewed === undefined ||
                          localFilter.reviewed === false
                        }
                        onClick={() =>
                          this.handleChangeFilter({
                            reviewed: true,
                          })
                        }
                        id="review-yes"
                        key="review-yes"
                      >
                        {t('ad.filters.reviewed.yes')}
                      </Button>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <div
                      className={
                        !localFilter.reviewed ? classes.disabledContent : ''
                      }
                    />
                    <div
                      className={`${classes.section} ${classes.reviewSection}`}
                    >
                      <h6 className={classes.title}>
                        {t('ad.filters.reviewed.by')}
                      </h6>
                      <Select
                        data-cy="adsFilterReviewedSelect"
                        id="select-reviewer"
                        MenuProps={{
                          className: classes.selectMenu,
                        }}
                        classes={{
                          select: classes.select,
                        }}
                        value={
                          localFilter.reviewerId === undefined
                            ? t('ad.filters.reviewed.anybody')
                            : localFilter.reviewerId
                        }
                        onChange={e => {
                          const { value } = e.target;
                          this.handleChangeFilter({
                            reviewerId: value,
                          });
                        }}
                        inputProps={{
                          name: 'selectedReviewedBy',
                          id: 'selectedReviewedBy',
                        }}
                        className={classes.reviewDD}
                      >
                        <MenuItem
                          key="reviewed-ad-anyone"
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={t('ad.filters.reviewed.anybody')}
                        >
                          {t('ad.filters.reviewed.anybody')}
                        </MenuItem>
                        {users.edges.map(edge => (
                          <MenuItem
                            key={edge.node.id}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={edge.node.id}
                          >
                            {edge.node.fullname}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>

                    <div className={classes.section}>
                      <h6 className={classes.title}>
                        {t('ad.filters.reviewed.date')}
                      </h6>
                      <GridContainer>
                        <GridItem xs={12} sm={12} md={6}>
                          <Datepicker
                            data-cy="adsFilterReviewedOnDateFrom"
                            title={t('ad.filters.date.from')}
                            defaultValue={t('ad.filters.date.from_placeholder')}
                            onChange={date => {
                              if (
                                localFilter.reviewedOnLte &&
                                localFilter.reviewedOnLte < date
                              ) {
                                return;
                              }
                              this.handleChangeFilter({
                                reviewedOnGte: date
                                  ? moment(date)
                                      .startOf('day')
                                      .format()
                                  : null,
                              });
                            }}
                            value={
                              localFilter.reviewedOnGte !== undefined &&
                              localFilter.reviewedOnGte !== null
                                ? moment(localFilter.reviewedOnGte)
                                : ''
                            }
                            toToday
                            validGte={localFilter.reviewedOnLte}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={6}>
                          <Datepicker
                            data-cy="adsFilterReviewedOnDateTo"
                            title={t('ad.filters.date.to')}
                            defaultValue={t('ad.filters.date.to_placeholder')}
                            onChange={date => {
                              if (
                                localFilter.reviewedOnGte &&
                                localFilter.reviewedOnGte > date
                              ) {
                                return;
                              }
                              this.handleChangeFilter({
                                reviewedOnLte: date
                                  ? moment(date)
                                      .endOf('day')
                                      .format()
                                  : null,
                              });
                            }}
                            value={
                              localFilter.reviewedOnLte !== undefined &&
                              localFilter.reviewedOnLte !== null
                                ? moment(localFilter.reviewedOnLte)
                                : ''
                            }
                            toToday
                            validLte={localFilter.reviewedOnGte}
                          />
                        </GridItem>
                      </GridContainer>
                    </div>
                  </div>
                </GridItem>
              </SectionDivider>
            </GridItem>
          </WithPermission>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Button
              color="primary"
              simple
              className={classes.buttons}
              onClick={this.expandAnimation}
              id="hideFilters"
              key="hideFilters"
            >
              <ArrowUp />
              {t('ad.filters.shrink')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    const defaultDateFilters = {
                      updatedOnGte: moment()
                        .subtract(3, 'day')
                        .startOf('day')
                        .format(),
                      updatedOnLte: moment()
                        .endOf('day')
                        .format(),
                      reviewedOnGte: undefined,
                      reviewedOnLte: undefined,
                    };
                    this.setState({
                      selectedAdsStatus: defaultSelectedStatus,
                      localFilter: { ...defaultDateFilters },
                    });
                    restartFilters();
                    this.expandAnimation();
                  }}
                  id="restartFilters"
                  key="restartFilters"
                >
                  {t('ad.filters.clean')}
                </Button>
                <Button
                  data-cy="filterAds"
                  color="primary"
                  className={classes.buttons}
                  onClick={() => {
                    this.expandAnimation();
                    this.handleFilter();
                  }}
                  id="filterAds"
                  key="filterAds"
                >
                  {t('ad.filters.filter')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  /**
   * @description
   * Render collapsed filter's box.
   * @returns {*} - AdFilter's box.
   */
  renderCollapseFilters() {
    const { expandFiltersAnimation } = this.state;
    const {
      classes,
      appliedFilter,
      categories,
      users,
      t,
      provinces,
    } = this.props;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation
          ? classes.expandCollapsedFilters
          : classes.collapseCollapsedFilters
      }`}
      >
        <div className={classes.filters}>
          {this.renderStringFilters({
            ip: [null, ''],
            botScore: [BotScoreIco, ''],
            titleDescription: [null, ''],
            id: [null, ''],
            contactInfo: [Person, ''],
            userId: [Person, ''],
            emailTerm: [Person, ''],
            phoneTerm: [Person, ''],
            sameOwnerAds: [BotScoreIco, ''],
          })}
          {this.renderMultipleFilters([
            'statusIn',
            'multipleSelectRules',
            'rulesParams',
          ])}
          {this.renderBoolFilters({
            hasUser: [
              t('ad.filters.tags.with_user'),
              t('ad.filters.tags.without_user'),
              Person,
            ],
            auto: [
              t('ad.filters.tags.ar_only'),
              t('ad.filters.tags.ar_no'),
              null,
            ],
            reviewed: [
              t('ad.filters.tags.review'),
              t('ad.filters.tags.review_no'),
              Eye,
            ],
            hasImage: [t('ad.filters.tags.hasImage'), '', Picture],
            hasFlag: ['reportados', '', Picture],
          })}
          {this.renderEntityFilters({
            category: [
              id => categories.find(item => item.id === id).title,
              null,
            ],
            subcategory: [
              id =>
                appliedFilter.category && id
                  ? categories
                      .find(item => item.id === appliedFilter.category)
                      .subcategories.find(item => item.id === id).title
                  : '',
              null,
            ],
            province: [
              id => provinces.find(item => item.id === id).name,
              Location,
            ],
            municipality: [
              id =>
                appliedFilter.province && id
                  ? provinces
                      .find(item => item.id === appliedFilter.province)
                      .municipalities.find(item => item.id === id).name
                  : '',
              Location,
            ],
            reviewerId: [
              id => users.edges.find(item => item.node.id === id).node.fullname,
              Eye,
            ],
          })}
          {this.renderDateTimeFilters(['updatedOn', 'reviewedOn'])}
        </div>
        <GridContainer>
          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <Button
              color="primary"
              simple
              className={classes.showFilters}
              onClick={this.expandAnimation}
              id="showFilters"
              key="showFilters"
              fullWidth
            >
              <ArrowDown />
              {t('ad.filters.expand')}
            </Button>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  /**
   * @description
   * Render collapsed or full filter's box.
   * @returns {*} - Filters.
   */
  render() {
    const { showCollapseFilters } = this.props;
    return (
      <Card>
        <CardBody>
          {!showCollapseFilters
            ? this.renderFilters()
            : this.renderCollapseFilters()}
        </CardBody>
      </Card>
    );
  }
}

/**
 * AdFilter PropTypes
 * @param classes - styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param showCollapseFilters - prop used to show full or collapsed filters
 * @param t - prop from HOC with string translations
 * @param categories - prop know categories and subcategories
 * @param provinces - prop know provinces and municipalities
 * @callback filter - callback called when user clicks filter button
 * @callback showFilters - callback called when user collapse filters button
 * @callback restartFilters - callback called when user restart filters button
 */

AdFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  showCollapseFilters: PropTypes.bool.isRequired,
  filter: PropTypes.func.isRequired,
  showFilters: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  users: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(FilterStyles)(AdFilter));
